import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trackingInputs, setTrackingInputs] = useState({});

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/orders/admin/all`);
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status, trackingInfo = null) => {
    try {
      await axios.put(`${BACKEND_URL}/api/orders/${orderId}/status`, {
        status,
        tracking_info: trackingInfo
      });
      
      toast.success(`Order marked as ${status}!`);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-900/50 text-yellow-400 border-yellow-600',
      preparing: 'bg-blue-900/50 text-blue-400 border-blue-600',
      shipped: 'bg-green-900/50 text-green-400 border-green-600',
      delivered: 'bg-gray-900/50 text-gray-400 border-gray-600'
    };
    
    return (
      <Badge className={`${colors[status] || colors.pending} border`}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p>Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-5xl font-black mb-8 text-center">
          Admin <span className="text-orange-500">Order Management</span>
        </h1>

        {orders.length === 0 ? (
          <p className="text-center text-gray-400">No orders yet</p>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <Card key={order.id} className="bg-gray-900 border-gray-800">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-white text-xl">
                        Order #{order.id.substring(0, 8)}
                      </CardTitle>
                      <p className="text-gray-400 text-sm">
                        {new Date(order.created_at).toLocaleString()}
                      </p>
                    </div>
                    {getStatusBadge(order.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Customer Info */}
                    <div>
                      <h3 className="font-bold text-orange-400 mb-2">Customer</h3>
                      <p className="text-white"><strong>Username:</strong> {order.username}</p>
                      <p className="text-gray-400"><strong>Email:</strong> {order.user_email}</p>
                      <p className="text-gray-400 mt-2"><strong>Shipping:</strong> {order.shipping_method}</p>
                    </div>

                    {/* Order Details */}
                    <div>
                      <h3 className="font-bold text-orange-400 mb-2">Items</h3>
                      {order.items.map((item, idx) => (
                        <p key={idx} className="text-gray-300">
                          {item.name} x{item.quantity} - ${item.price.toFixed(2)}
                        </p>
                      ))}
                      <p className="text-white font-bold mt-2">
                        Total: ${order.total_amount.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Order Actions */}
                  <div className="mt-6 pt-6 border-t border-gray-800">
                    <h3 className="font-bold text-white mb-4">Update Status</h3>
                    <div className="flex flex-wrap gap-3 mb-4">
                      <Button
                        size="sm"
                        onClick={() => updateOrderStatus(order.id, 'preparing')}
                        disabled={order.status === 'preparing'}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Mark Preparing
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateOrderStatus(order.id, 'shipped', trackingInputs[order.id])}
                        disabled={order.status === 'shipped'}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Mark Shipped
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateOrderStatus(order.id, 'delivered')}
                        disabled={order.status === 'delivered'}
                        className="bg-gray-600 hover:bg-gray-700"
                      >
                        Mark Delivered
                      </Button>
                    </div>

                    {/* Tracking Info */}
                    <div className="flex gap-3">
                      <Input
                        placeholder="Tracking number (optional)"
                        value={trackingInputs[order.id] || ''}
                        onChange={(e) => setTrackingInputs({
                          ...trackingInputs,
                          [order.id]: e.target.value
                        })}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    {order.tracking_info && (
                      <p className="text-sm text-gray-400 mt-2">
                        Current tracking: {order.tracking_info}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
